import { useEffect, useMemo } from 'react'
import { useObjectFlagValue } from '@openfeature/react-sdk'

import {
  addCurrencyArray,
  centsToCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { CancellationFlowStaticValues } from '@/features/user/settings/sections/self-serve-cancellation/lib/helpers'
import { getTransactionsByTypeAndYear } from '@/features/transactions/transactions.selectors'
import { useAnalyticsTrack } from '@/features/amplitude'
import { Text, Card, Link, CardColors, Popover, Icon } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

export const TaxDeductionCard = () => {
  const track = useAnalyticsTrack()
  const cancellationSaveFlowStaticValues: CancellationFlowStaticValues =
    useObjectFlagValue(FEATURE_FLAG_KEYS.cancellationSaveFlowStaticValues, {})

  const currentYear = new Date().getFullYear()

  const businessTransactions = useReselector(
    getTransactionsByTypeAndYear,
    'fullyDeductibleBusinessExpenses',
    currentYear
  )

  const uncategorizedTransactions = useReselector(
    getTransactionsByTypeAndYear,
    'uncategorizedNonPersonalTransactions',
    currentYear
  )

  const needsReviewTransactions = useReselector(
    getTransactionsByTypeAndYear,
    'needsReview',
    currentYear
  )

  const totalUncategorizedTransactionsInCents = useMemo(
    () =>
      addCurrencyArray(
        uncategorizedTransactions.map(({ amountInCents }) =>
          Math.abs(amountInCents)
        )
      ),
    [uncategorizedTransactions]
  )

  const totalTransactionsInCents = useMemo(
    () =>
      addCurrencyArray(
        businessTransactions.map(({ amountInCents }) => amountInCents)
      ),
    [businessTransactions]
  )

  const effectiveTaxRate =
    cancellationSaveFlowStaticValues.effective_tax_rate || 0.3

  const moneySaved = useMemo(
    () =>
      centsToCurrency(totalTransactionsInCents).multiply(effectiveTaxRate)
        .intValue,
    [totalTransactionsInCents, effectiveTaxRate]
  )

  const hasTransactionsToReview = Boolean(uncategorizedTransactions.length)

  useEffect(() => {
    if (hasTransactionsToReview) {
      track("Viewed Page 'tax reduction transactions to review'")
    }
  }, [hasTransactionsToReview, track])

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Estimated Tax Savings */}
        <div className="lg:w-1/2">
          <Text variant={TypographyStyles.H2}>
            {formatCurrencyFromCents(moneySaved * -1)}
          </Text>
          <div className="flex items-start gap-1">
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
              est {currentYear} tax savings
            </Text>
            <Popover>
              <Popover.Trigger asChild>
                <Icon
                  icon={regular('question-circle')}
                  className="ml-1 cursor-pointer"
                />
              </Popover.Trigger>
              <Popover.Content className="max-w-md space-y-2">
                <Text variant={TypographyStyles.H3}>Estimated tax savings</Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  This calculation provides an approximate tax savings
                  projection by applying a standard {effectiveTaxRate * 100}%
                  tax rate to your total deductible expenses, giving you a
                  general idea of the potential reduction in your tax liability.
                </Text>
              </Popover.Content>
            </Popover>
          </div>
        </div>

        {/* Deductible & Uncategorized */}
        <div className="lg:w-1/2 space-y-4">
          <div>
            <Text variant={TypographyStyles.H2}>
              {formatCurrencyFromCents(totalTransactionsInCents * -1)}
            </Text>
            <div className="flex items-start gap-1">
              <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
                {currentYear} tax deductible expenses
              </Text>
              <Popover>
                <Popover.Trigger asChild>
                  <Icon
                    icon={regular('question-circle')}
                    className="ml-1 cursor-pointer"
                  />
                </Popover.Trigger>
                <Popover.Content className="max-w-md space-y-2">
                  <Text variant={TypographyStyles.H3}>
                    Total deductible expenses
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM}>
                    This number represents the total dollar amount in
                    transactions that have been confirmed as tax deductible
                    expenses by your bookkeeper. When you file your annual
                    taxes, these deductions will lower your taxable income,
                    resulting in a smaller tax bill.
                  </Text>
                </Popover.Content>
              </Popover>
            </div>
          </div>

          <div>
            <Text variant={TypographyStyles.H2} color={TextColors.ORANGE}>
              {formatCurrencyFromCents(totalUncategorizedTransactionsInCents)}
            </Text>
            <div className="flex items-start gap-1">
              <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
                uncategorized transactions
              </Text>
              <Popover>
                <Popover.Trigger asChild>
                  <Icon
                    icon={regular('question-circle')}
                    className="ml-1 cursor-pointer"
                  />
                </Popover.Trigger>
                <Popover.Content className="max-w-md space-y-2">
                  <Text variant={TypographyStyles.H3}>
                    Uncategorized transactions
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM}>
                    This number represents the total dollar amount of
                    transactions that have yet to be categorized.
                  </Text>
                </Popover.Content>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Needs Review Alert */}
      {Boolean(needsReviewTransactions.length) && (
        <Card color={CardColors.PRIORITY}>
          <Text>
            Find more tax deduction savings by reviewing{' '}
            {needsReviewTransactions.length} transactions.
          </Text>
          <Link
            href="/transactions/review"
            trackMessage="Clicked Tax Reductions Transactions to Review"
            className="text-blue-600 underline"
          >
            Go →
          </Link>
        </Card>
      )}
    </Card>
  )
}
